import { VirtualScroll } from '@/utils/VirtualScroll'
import { mapper } from '@/utils/Maths'
import gsap from 'gsap'

const duration = 3

export class CameraMovement {
  constructor (camera, cameraPos, transition = 'power3.inOut', useDistance = false) {
    this.setCameraPosition = this.setCameraPosition.bind(this)

    this.onNext = this.onNext.bind(this)
    this.onPrev = this.onPrev.bind(this)
    this.onScroll = this.onScroll.bind(this)

    this.look = camera.look
    this.camera = camera
    this.cameraPos = cameraPos ? cameraPos : this.camera.position
    this.cameraLine = []
    this._progression = 0
    this.absoluteProgression = 0
    this.valueStart = 0
    this._step = 0
    this.stepProgression = 10

    this.callbacksStart = []
    this.callbacksNextEnd = []
    this.callbacksPrevEnd = []
    this._transition = transition
    this.isTransition = false
  }

  onNext () {
    if (this._transition !== 'free' && !this.isTransition) {
      this.isTransition = true
      this._onStart()

      const nextStep = this.getNextIndex(this._step)

      this.gsapPos = gsap.to(this.cameraPos, {
        x: this.cameraLine[nextStep].position.x,
        y: this.cameraLine[nextStep].position.y,
        z: this.cameraLine[nextStep].position.z,
        duration: duration,
        ease: this._transition,
        onComplete: () => {
          this.isTransition = false
          this._onNextEnd() // call callbacks end
        }
      })
      this.gsapLook = gsap.to(this.look, {
        x: this.cameraLine[nextStep].lookAt.x,
        y: this.cameraLine[nextStep].lookAt.y,
        z: this.cameraLine[nextStep].lookAt.z,
        duration: duration,
        ease: this._transition,
        onComplete: () => {
          this.isTransition = false
        }
      })
    }
  }

  onPrev () {
    if (this._transition !== 'free' && !this.isTransition) {
      this.isTransition = true
      this._onStart()

      const prevStep = this.getPrevIndex(this._step)

      this.gsapPos = gsap.to(this.cameraPos, {
        x: this.cameraLine[prevStep].position.x,
        y: this.cameraLine[prevStep].position.y,
        z: this.cameraLine[prevStep].position.z,
        duration: duration,
        ease: this._transition,
        onComplete: () => {
          this.isTransition = false
          this._onPrevEnd() // call callbacks end
        }
      })
      this.gsapLook = gsap.to(this.look, {
        x: this.cameraLine[prevStep].lookAt.x,
        y: this.cameraLine[prevStep].lookAt.y,
        z: this.cameraLine[prevStep].lookAt.z,
        duration: duration,
        ease: this._transition,
        onComplete: () => {
          this.isTransition = false
        }
      })
    }
  }

  onScroll ({ value }) {
    this.distanceFromIndex(0)
    if (this._transition === 'free') {
      this.absoluteProgression = value * .005
      this.step = Math.floor(this.absoluteProgression / this.stepProgression) % this.cameraLine.length

      if (!this.isTransition) {
        this.valueStart = value
        this.isTransition = true

        const nextStep = this.getNextIndex(this._step)

        this.gsapPos = gsap.fromTo(this.cameraPos, {
          x: this.cameraLine[this._step].position.x,
          y: this.cameraLine[this._step].position.y,
          z: this.cameraLine[this._step].position.z,
        }, {
          x: this.cameraLine[nextStep].position.x,
          y: this.cameraLine[nextStep].position.y,
          z: this.cameraLine[nextStep].position.z,
          duration: this.stepProgression,
          ease: 'power2.inOut'
        }).pause()
        this.gsapLook = gsap.fromTo(this.look, {
          x: this.cameraLine[this._step].lookAt.x,
          y: this.cameraLine[this._step].lookAt.y,
          z: this.cameraLine[this._step].lookAt.z,
        },{
          x: this.cameraLine[nextStep].lookAt.x,
          y: this.cameraLine[nextStep].lookAt.y,
          z: this.cameraLine[nextStep].lookAt.z,
          duration: this.stepProgression,
          ease: 'power2.inOut'
        }).pause()
      }

      const progression = this.absoluteProgression % this.stepProgression
      this.gsapPos.seek(progression)
      this.gsapLook.seek(progression)

      this._progression = progression / this.stepProgression
    }
  }

  setCameraPosition (index, cameraPos) {
    this.cameraLine[index] = cameraPos
  }

  _onStart () {
    this.callbacksStart.forEach(callback => callback())
  }

  _onNextEnd () {
    this.step = this.getNextIndex(this._step)
    this.callbacksNextEnd.forEach(callback => callback())
  }

  _onPrevEnd () {
    this.step = this.getPrevIndex(this._step)
    this.callbacksPrevEnd.forEach(callback => callback())
  }

  distanceFromIndex (index) {
    // does not work for last item
    const modulo = this.absoluteProgression % (this.stepProgression * this.cameraLine.length)
    const val1 = modulo - this.stepProgression * index
    const val2 = modulo - this.stepProgression * (index + this.cameraLine.length)
    const value = Math.abs(val1) < Math.abs(val2) ? val1 : val2

    return value / this.stepProgression
  }

  onStart () {
    this.callbacksStart.push(callback)
  }

  onNextEnd (callback) {
    this.callbacksNextEnd.push(callback)
  }

  onPrevEnd (callback) {
    this.callbacksPrevEnd.push(callback)
  }

  getPrevIndex (i) {
    return i === 0 ? this.cameraLine.length - 1 : i - 1
  }

  getNextIndex (i) {
    return (i + 1) % this.cameraLine.length
  }

  set step (step) {
    if (step === this._step) {return}

    this.isTransition = false

    if(this.gsapPos) {
      this.gsapPos.kill()
      this.gsapLook.kill()
    }

    this._step = step
  }

  get step () {
    return this._step
  }

  set cameraPositions (cameraPositions) {
    this.cameraLine = [...cameraPositions]
  }

  get cameraPositions () {
    return [...this.cameraLine]
  }

  get transition() {
    return this._transition
  }

  set transition(transition) {
    this._transition = transition
  }
}
